import React from "react";
import { PageTemplate } from "@components/pageTemplate/PageTemplate";
import Typography from "../../components/typography/Typography";
import Tabs from "../../components/tabs/Tabs";
import Tab from "../../components/tabs/Tab";
import Card from "../../components/card/Card";
import { useTheme } from "@emotion/react";
import { flexColumn, flexRow } from "../../reusableStyles/flex";
import { gap } from "../../reusableStyles/gap";
import Chart from "react-apexcharts";

const ModelPage = () => {
  const theme = useTheme();

  return (
    <PageTemplate
      title="Model Details"
      breadcrumbs={[
        { label: "Admin", path: "/admin" },
        { label: "ML Models", path: "/admin/ml-models" },
        { label: "Model Details", path: "/admin/ml-models/model" },
      ]}
      subtitle="Model Version: v2"
      activeItem="admin"
      activeSubItem="ML Models"
      actions={[
        {
          text: "Default model",
          action: () => {
            /**/
          },
          color: "primary",
          disabled: true,
        },
      ]}
    >
      <Tabs>
        <Tab label="Overview" key="overview" extraCss={[flexColumn, gap(8)]}>
          <div css={[flexRow, gap(8)]}>
            <Card subtitle="Train date" stretch compact>
              <Typography text="2023-04-04" size="sm" weight="medium" />
            </Card>
            <Card subtitle="Bins" stretch compact>
              <Typography text="8" size="sm" weight="medium" />
            </Card>
            <Card subtitle="Samples" stretch compact gap={2}>
              <Typography text="283 total" size="sm" weight="medium" />
              <Typography text="200 train" size="sm" weight="medium" />
              <Typography text="83 validation" size="sm" weight="medium" />
            </Card>
            <Card subtitle="Train time" stretch compact>
              <Typography
                text="6h per bin (per voter)"
                size="sm"
                weight="medium"
              />
            </Card>
            <Card subtitle="Num voters" stretch compact>
              <Typography text="8" size="sm" weight="medium" />
            </Card>
            <Card subtitle="Version" stretch compact>
              <Typography text="v1" size="sm" weight="medium" />
            </Card>
          </div>
          <Card title="Overall Accuracies" fullWidth>
            <Chart
              series={[
                {
                  name: "Training",
                  data: [91, 87, 88, 82, 85, 89, 81, 84, 80, 91, 86],
                },
                {
                  name: "Validation",
                  data: [69, 71, 70, 72, 73, 74, 70, 67, 64, 69, 71],
                },
              ]}
              height={350}
              type="bar"
              options={{
                chart: {
                  fontFamily: "Inter",
                },
                dataLabels: {
                  enabled: false,
                },
                stroke: {
                  show: true,
                  width: 2,
                  colors: ["transparent"],
                },
                xaxis: {
                  categories: [
                    "Microbial Biomass",
                    "Respiration",
                    "Organic Matter",
                    "pH",
                    "Extractable Nitrate",
                    "Extractable Ammonium",
                    "WHC",
                    "Field Water Content",
                    "Available P",
                    "Available K",
                    "Available Mg",
                  ],
                  labels: {
                    hideOverlappingLabels: false,
                    rotateAlways: true,
                  },
                  title: {
                    text: "Indicator",
                    style: {
                      fontWeight: 500,
                    },
                  },
                },
                yaxis: {
                  title: {
                    text: "Accuracy (%)",
                    style: {
                      fontWeight: 500,
                    },
                  },
                },
                fill: {
                  opacity: 1,
                },
                tooltip: {
                  y: {
                    formatter: (val: number) => `${val}%`,
                  },
                },
                colors: [
                  theme.colours.primary["700"],
                  theme.colours.greyBlue["400"],
                ],
              }}
            />
          </Card>
        </Tab>
        <Tab label="Microbial Biomass" key="biomass">
          <Card fullWidth title="Fitness">
            <Tabs>
              <Tab label="All" key="all"></Tab>
              <Tab label="Bin 1" key="bin1"></Tab>
              <Tab label="Bin 2" key="bin2"></Tab>
              <Tab label="Bin 3" key="bin3"></Tab>
              <Tab label="Bin 4" key="bin4"></Tab>
              <Tab label="Bin 5" key="bin5"></Tab>
              <Tab label="Bin 6" key="bin6"></Tab>
              <Tab label="Bin 7" key="bin7"></Tab>
              <Tab label="Bin 8" key="bin8"></Tab>
            </Tabs>
          </Card>
        </Tab>
        <Tab label="Respiration" key="respiration">
          <Card title="Overall Accuracies" fullWidth>
            <Chart
              series={[
                {
                  name: "Training",
                  data: [
                    [1, 100],
                    [2, 90],
                    [3, 80],
                    [10, 50],
                  ],
                },
                {
                  name: "Training",
                  data: [
                    [1, 200],
                    [2, 170],
                    [3, 150],
                    [8, 120],
                    [10, 100],
                  ],
                },
              ]}
              height={450}
              type="line"
              options={{
                chart: {
                  fontFamily: "Inter",
                },
                dataLabels: {
                  enabled: false,
                },
                stroke: {
                  curve: "smooth",
                  width: 6,
                },
                xaxis: {
                  labels: {
                    hideOverlappingLabels: false,
                    rotateAlways: true,
                  },
                  title: {
                    text: "Indicator",
                    style: {
                      fontWeight: 500,
                    },
                  },
                },
                yaxis: {
                  title: {
                    text: "Accuracy (%)",
                    style: {
                      fontWeight: 500,
                    },
                  },
                },
                fill: {
                  type: "gradient",
                  gradient: {
                    shade: "dark",
                    gradientToColors: [
                      theme.colours.primary["500"],
                      theme.colours.purple["400"],
                    ],
                    shadeIntensity: 1,
                    type: "horizontal",
                    opacityFrom: 1,
                    opacityTo: 1,
                    stops: [0, 100, 100, 100],
                  },
                },
                tooltip: {
                  y: {
                    formatter: (val: number) => `${val}%`,
                  },
                },
                colors: [
                  theme.colours.primary["700"],
                  theme.colours.purple["600"],
                ],
              }}
            />
          </Card>
        </Tab>
        <Tab label="Organic Matter" key="matter">
          <Typography text="Organic Matter" />
        </Tab>
        <Tab label="Extractable Nitrate" key="nitrate">
          <Typography text="Extractable Nitrate" />
        </Tab>
        <Tab label="Extractable Ammonium" key="ammonium">
          <Typography text="Extractable Ammonium" />
        </Tab>
        <Tab label="WHC" key="whc">
          <Typography text="WHC" />
        </Tab>
        <Tab label="Field Water Content" key="fwc">
          <Typography text="Field Water Content" />
        </Tab>
        <Tab label="Mg Available" key="mg">
          <Typography text="Mg Available" />
        </Tab>
        <Tab label="K Available" key="k">
          <Typography text="K Available" />
        </Tab>
        <Tab label="P Available" key="P">
          <Typography text="P Available" />
        </Tab>
        <Tab label="pH" key="ph">
          <Typography text="pH" />
        </Tab>
      </Tabs>
    </PageTemplate>
  );
};

export default ModelPage;
